/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Month-grid availability calendar with INTERVAL (range) selection. Days that are booked (for a car)
 * or fully booked across the model (no car free) are disabled. The renter clicks a start day then an
 * end day to pick a continuous free interval. Month/weekday names follow the active i18n language.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  busyDays: Set<string>;          // yyyy-MM-dd that must be disabled
  from?: string;                  // selected start yyyy-MM-dd
  to?: string;                    // selected end yyyy-MM-dd
  onChange: (from: string, to: string) => void;
  minDate?: string;               // yyyy-MM-dd, default today
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => iso(new Date());

export default function AvailabilityCalendar({ busyDays, from, to, onChange, minDate }: Props) {
  const { i18n } = useTranslation();
  const min = minDate || todayIso();

  const start = from || min;
  const [view, setView] = useState(() => {
    const d = new Date(start + 'T00:00:00');
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const monthLabel = useMemo(() => {
    const d = new Date(view.y, view.m, 1);
    return new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(d);
  }, [view, i18n.language]);

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
    // Week starts Sunday (index 0).
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2026, 10, 1 + i))); // 2026-11-01 is a Sunday
  }, [i18n.language]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = first.getDay(); // 0=Sun
    const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
    const out: (string | null)[] = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(iso(new Date(view.y, view.m, d)));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const inRange = (day: string) => from && to && day > from && day < to;
  const isBusy = (day: string) => busyDays.has(day);
  const isPast = (day: string) => day < min;

  /** Does the open interval (a, b) contain a busy day? Used to reject ranges that cross a booking. */
  const rangeHasBusy = (a: string, b: string) => {
    for (const bd of busyDays) if (bd > a && bd < b) return true;
    return false;
  };

  const pick = (day: string) => {
    if (isBusy(day) || isPast(day)) return;
    // No start yet, or a full range already chosen → start a new range.
    if (!from || (from && to)) {
      onChange(day, '');
      return;
    }
    // Have a start, choosing the end.
    if (day < from) { onChange(day, ''); return; }
    if (day === from) return;
    if (rangeHasBusy(from, day)) { onChange(day, ''); return; } // crosses a booking → restart from here
    onChange(from, day);
  };

  const prevDisabled = view.y === new Date(min + 'T00:00:00').getFullYear() && view.m <= new Date(min + 'T00:00:00').getMonth();

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))}
          disabled={prevDisabled}
          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 text-gray-700"
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm font-semibold text-gray-900 capitalize">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] text-gray-400 mb-1">
        {weekdays.map((w, i) => <div key={i} className="py-1 capitalize">{w}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const dn = Number(day.slice(8, 10));
          const disabled = isBusy(day) || isPast(day);
          const isFrom = day === from;
          const isTo = day === to;
          const selected = isFrom || isTo;
          const between = inRange(day);

          let cls = 'h-9 flex items-center justify-center text-sm rounded-md transition-colors ';
          if (disabled) cls += 'text-gray-300 line-through cursor-not-allowed';
          else if (selected) cls += 'bg-yellow-400 text-black font-bold';
          else if (between) cls += 'bg-yellow-100 text-gray-900';
          else cls += 'text-gray-900 hover:bg-gray-100 cursor-pointer';

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => pick(day)}
              className={cls}
            >
              {dn}
            </button>
          );
        })}
      </div>
    </div>
  );
}
